import { Home, FileText, Key, AlertTriangle, CheckCircle, Phone, ScrollText, ShieldCheck, Search, Gavel } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import CTASection from "@/components/CTASection";
import QuickContact from "@/components/QuickContact";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { LocalizedLink } from "@/components/LocalizedLink";
import { useLanguage } from "@/hooks/useLanguage";
import { RelatedLinks } from "@/components/RelatedLinks";
import AIReadableSection from "@/components/AIReadableSection";
import { realEstateFAQs } from "@/lib/practice-area-faqs";
import HeroSection from "@/components/HeroSection";
import realEstateHeroImage from "@/assets/real-estate-hero.jpg";

const RealEstateLaw = () => {
  const { language } = useLanguage();

  const content = {
    en: {
      hero: { title: "Real Estate Law", subtitle: "Protecting Your Property Rights & Investments in Texas", schedule: "Schedule Consultation", call: "Call" },
      services: { title: "Our Real Estate Law Services", subtitle: "Whether you're buying your first home or managing commercial properties, we provide comprehensive legal guidance for all your real estate needs." },
      items: [
        { title: "Buying & Selling Residential Property", description: "Comprehensive guidance through every step of your residential real estate transaction." },
        { title: "Title Search Services", description: "Comprehensive title examination to ensure clear property ownership." },
        { title: "Contract Review", description: "Thorough review of purchase agreements and real estate contracts." },
        { title: "Affidavits of Heirship", description: "Legal documentation for transferring property through inheritance." },
        { title: "Lien Release and Removal", description: "Resolution of property liens to clear title for sale or refinancing." },
        { title: "Landlord/Tenant Lease Negotiations", description: "Drafting and negotiating residential and commercial lease agreements." },
        { title: "Residential & Commercial Closings", description: "Full-service closing coordination for smooth property transfers." },
        { title: "Foreclosures & Evictions", description: "Legal representation for foreclosure defense and eviction proceedings." }
      ],
      process: { title: "Real Estate Closing Process" },
      steps: [
        { title: "Contract Review", desc: "Thorough review of purchase agreement and terms" },
        { title: "Title Examination", desc: "Complete title search and insurance" },
        { title: "Due Diligence", desc: "Property inspection and document verification" },
        { title: "Closing Preparation", desc: "Prepare all closing documents and disclosures" },
        { title: "Final Walkthrough", desc: "Coordinate final property inspection" },
        { title: "Closing Day", desc: "Execute documents and transfer ownership" }
      ],
      why: { title: "Why Choose Us for Real Estate Matters?" },
      whyItems: [
        { title: "Thorough Review", description: "We meticulously review every document to protect your interests" },
        { title: "Smooth Closings", description: "Efficient coordination for stress-free transactions" },
        { title: "Local Expertise", description: "Deep knowledge of Texas real estate law and Houston market" }
      ]
    },
    es: {
      hero: { title: "Derecho Inmobiliario", subtitle: "Protegiendo Sus Derechos de Propiedad e Inversiones en Texas", schedule: "Agendar Consulta", call: "Llamar" },
      services: { title: "Nuestros Servicios de Derecho Inmobiliario", subtitle: "Ya sea que esté comprando su primera casa o administrando propiedades comerciales, brindamos orientación legal experta para todas sus necesidades inmobiliarias." },
      items: [
        { title: "Compra y Venta de Propiedades Residenciales", description: "Orientación experta en cada paso de su transacción inmobiliaria residencial." },
        { title: "Servicios de Búsqueda de Título", description: "Examen integral de título para asegurar propiedad clara." },
        { title: "Revisión de Contratos", description: "Revisión exhaustiva de acuerdos de compra y contratos inmobiliarios." },
        { title: "Declaraciones Juradas de Herederos", description: "Documentación legal para transferir propiedad mediante herencia." },
        { title: "Liberación y Remoción de Gravámenes", description: "Resolución de gravámenes de propiedad para limpiar título para venta o refinanciamiento." },
        { title: "Negociaciones de Arrendamiento Propietario/Inquilino", description: "Redacción y negociación de acuerdos de arrendamiento residencial y comercial." },
        { title: "Cierres Residenciales y Comerciales", description: "Coordinación de cierre de servicio completo para transferencias de propiedad sin problemas." },
        { title: "Ejecuciones Hipotecarias y Desalojos", description: "Representación legal para defensa de ejecución hipotecaria y procedimientos de desalojo." }
      ],
      process: { title: "Proceso de Cierre Inmobiliario" },
      steps: [
        { title: "Revisión de Contrato", desc: "Revisión exhaustiva del acuerdo de compra y términos" },
        { title: "Examen de Título", desc: "Búsqueda completa de título y seguro" },
        { title: "Debida Diligencia", desc: "Inspección de propiedad y verificación de documentos" },
        { title: "Preparación de Cierre", desc: "Preparar todos los documentos de cierre y divulgaciones" },
        { title: "Inspección Final", desc: "Coordinar inspección final de propiedad" },
        { title: "Día de Cierre", desc: "Ejecutar documentos y transferir propiedad" }
      ],
      why: { title: "¿Por Qué Elegirnos para Asuntos Inmobiliarios?" },
      whyItems: [
        { title: "Revisión Exhaustiva", description: "Revisamos meticulosamente cada documento para proteger sus intereses" },
        { title: "Cierres Sin Problemas", description: "Coordinación eficiente para transacciones sin estrés" },
        { title: "Experiencia Local", description: "Conocimiento profundo de la ley inmobiliaria de Texas y el mercado de Houston" }
      ]
    },
    vi: {
      hero: { title: "Luật Bất Động Sản", subtitle: "Bảo Vệ Quyền Tài Sản & Đầu Tư Của Bạn tại Texas", schedule: "Đặt Lịch Tư Vấn", call: "Gọi" },
      services: { title: "Dịch Vụ Luật Bất Động Sản Của Chúng Tôi", subtitle: "Cho dù bạn đang mua ngôi nhà đầu tiên hay quản lý bất động sản thương mại, chúng tôi cung cấp hướng dẫn pháp lý chuyên nghiệp cho tất cả nhu cầu bất động sản của bạn." },
      items: [
        { title: "Mua & Bán Bất Động Sản Dân Cư", description: "Hướng dẫn chuyên nghiệp qua từng bước giao dịch bất động sản dân cư của bạn." },
        { title: "Dịch Vụ Tìm Kiếm Sở Hữu", description: "Kiểm tra sở hữu toàn diện để đảm bảo quyền sở hữu tài sản rõ ràng." },
        { title: "Xem Xét Hợp Đồng", description: "Xem xét kỹ lưỡng thỏa thuận mua và hợp đồng bất động sản." },
        { title: "Tuyên Thệ Thừa Kế", description: "Tài liệu pháp lý để chuyển giao tài sản thông qua thừa kế." },
        { title: "Giải Phóng và Gỡ Bỏ Khoản Nợ", description: "Giải quyết khoản nợ tài sản để làm sạch sở hữu cho bán hoặc tái cấp vốn." },
        { title: "Đàm Phán Thuê Chủ Nhà/Người Thuê", description: "Soạn thảo và đàm phán thỏa thuận thuê dân cư và thương mại." },
        { title: "Đóng Cửa Dân Cư & Thương Mại", description: "Điều phối đóng cửa dịch vụ đầy đủ cho chuyển giao tài sản suôn sẻ." },
        { title: "Tịch Thu & Trục Xuất", description: "Đại diện pháp lý cho phòng thủ tịch thu và thủ tục trục xuất." }
      ],
      process: { title: "Quy Trình Đóng Cửa Bất Động Sản" },
      steps: [
        { title: "Xem Xét Hợp Đồng", desc: "Xem xét kỹ lưỡng thỏa thuận mua và điều khoản" },
        { title: "Kiểm Tra Sở Hữu", desc: "Tìm kiếm sở hữu đầy đủ và bảo hiểm" },
        { title: "Thẩm Định Kỹ Lưỡng", desc: "Kiểm tra tài sản và xác minh tài liệu" },
        { title: "Chuẩn Bị Đóng Cửa", desc: "Chuẩn bị tất cả tài liệu đóng cửa và tiết lộ" },
        { title: "Kiểm Tra Cuối Cùng", desc: "Điều phối kiểm tra tài sản cuối cùng" },
        { title: "Ngày Đóng Cửa", desc: "Thực hiện tài liệu và chuyển giao quyền sở hữu" }
      ],
      why: { title: "Tại Sao Chọn Chúng Tôi Cho Vấn Đề Bất Động Sản?" },
      whyItems: [
        { title: "Xem Xét Kỹ Lưỡng", description: "Chúng tôi xem xét tỉ mỉ từng tài liệu để bảo vệ lợi ích của bạn" },
        { title: "Đóng Cửa Suôn Sẻ", description: "Điều phối hiệu quả cho giao dịch không căng thẳng" },
        { title: "Chuyên Môn Địa Phương", description: "Kiến thức sâu về luật bất động sản Texas và thị trường Houston" }
      ]
    }
  };

  const pageContent = content[language];
  const serviceRoutes = [
    "/practice-areas/real-estate-law/buying-selling",
    "/practice-areas/real-estate-law/title-search",
    "/practice-areas/real-estate-law/contract-review",
    "/practice-areas/real-estate-law/affidavits-heirship",
    "/practice-areas/real-estate-law/lien-release",
    "/practice-areas/real-estate-law/lease-negotiations",
    "/practice-areas/real-estate-law/closings",
    "/practice-areas/real-estate-law/foreclosures-evictions"
  ];

  return (
    <div className="min-h-screen">
      <Header />

      {/* Hero Section - Optimized for CWV */}
      <HeroSection
        imageSrc={realEstateHeroImage}
        imageAlt="Real Estate Lawyer Houston - Title, Closings, Transactions"
        height="60vh"
        overlayGradient="bg-gradient-to-r from-primary-dark/95 via-primary/90 to-primary-dark/95"
        priority={true}
      >
        <div className="text-center text-white">
          <h1 className="text-5xl md:text-6xl font-bold mb-6">{pageContent.hero.title}</h1>
          <p className="text-xl md:text-2xl mb-8 max-w-3xl mx-auto">{pageContent.hero.subtitle}</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button asChild size="lg" variant="cta">
              <LocalizedLink to="/appointment">{pageContent.hero.schedule}</LocalizedLink>
            </Button>
            <Button asChild size="lg" variant="outline" className="border-white text-white hover:bg-white/20">
              <a href="tel:8329303059"><Phone className="mr-2 h-5 w-5" />{pageContent.hero.call}: (832) 930-3059</a>
            </Button>
          </div>
        </div>
      </HeroSection>

      <section className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">{pageContent.services.title}</h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">{pageContent.services.subtitle}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
            {pageContent.items.map((service, index) => (
              <Card key={index} className="hover:shadow-xl transition-all border-accent/20 hover:border-accent/50">
                <CardHeader>
                  <div className="text-accent mb-4">
                    {index === 0 ? <Home className="h-8 w-8" /> : index === 1 ? <Search className="h-8 w-8" /> : index === 2 ? <FileText className="h-8 w-8" /> : index === 3 ? <ScrollText className="h-8 w-8" /> : index === 4 ? <ShieldCheck className="h-8 w-8" /> : index === 5 ? <Key className="h-8 w-8" /> : index === 6 ? <CheckCircle className="h-8 w-8" /> : <Gavel className="h-8 w-8" />}
                  </div>
                  <CardTitle>{service.title}</CardTitle>
                  <CardDescription>{service.description}</CardDescription>
                </CardHeader>
                <CardContent>
                  <Button asChild variant="outline" className="w-full">
                    <LocalizedLink to={serviceRoutes[index]}>Más Información</LocalizedLink>
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <QuickContact />

      <section className="py-20 bg-muted/30">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-center mb-12">{pageContent.process.title}</h2>
          <div className="max-w-4xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-6">
            {pageContent.steps.map((step, index) => (
              <div key={index} className="flex gap-4 items-start bg-card p-6 rounded-lg shadow-md">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-gradient-gold-shine text-primary font-bold flex items-center justify-center">{index + 1}</div>
                <div>
                  <h3 className="text-lg font-bold mb-1">{step.title}</h3>
                  <p className="text-sm text-muted-foreground">{step.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-center mb-12">{pageContent.why.title}</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {pageContent.whyItems.map((item, index) => (
              <div key={index} className="text-center p-6">
                <div className="text-accent mb-4 flex justify-center">
                  {index === 0 ? <FileText className="h-12 w-12" /> : index === 1 ? <Key className="h-12 w-12" /> : <Home className="h-12 w-12" />}
                </div>
                <h3 className="text-xl font-bold mb-2">{item.title}</h3>
                <p className="text-muted-foreground">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <AIReadableSection 
        title={realEstateFAQs[language].title}
        summary={realEstateFAQs[language].summary}
      />

      <RelatedLinks category="realEstate" />

      <CTASection />
      <Footer />
    </div>
  );
};

export default RealEstateLaw;
